/**
 * Household Service
 * 
 * Centralized business logic for household management:
 * - Send invites (via edge function → email notification)
 * - Cancel invites
 * - Load household data (members + pending invites)
 * 
 * Extracted from SettingsPage to enable testing and reuse.
 */

import { supabase } from '@/lib/supabase/client';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface HouseholdMember {
  id: string;
  profile_id: string;
  role: string;
  joined_at: string;
  profiles: { name: string; email: string | null };
}

export interface HouseholdInvite {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
  expires_at: string | null;
  household_id: string;
}

export interface HouseholdData {
  householdId: string | null;
  members: HouseholdMember[];
  invites: HouseholdInvite[];
}

export interface InviteResult {
  success: boolean;
  error?: string;
  isDuplicate?: boolean;
  emailSent?: boolean;
}

// -----------------------------------------------------------------------------
// Load household data
// -----------------------------------------------------------------------------

export async function loadHouseholdData(profileId: string): Promise<HouseholdData> {
  // Find which household the user belongs to
  const { data: memberships, error: memError } = await supabase
    .from('household_members')
    .select('household_id')
    .eq('profile_id', profileId);

  if (memError || !memberships || memberships.length === 0) {
    return { householdId: null, members: [], invites: [] };
  }

  const householdId = memberships[0].household_id;

  // Load members and pending invites in parallel
  const [membersResult, invitesResult] = await Promise.all([
    supabase
      .from('household_members')
      .select('id, profile_id, role, joined_at, profiles:profile_id(name, email)')
      .eq('household_id', householdId),
    supabase
      .from('household_invites')
      .select('id, email, name, role, created_at, expires_at, household_id')
      .eq('household_id', householdId)
      .is('accepted_at', null)
      .or('expires_at.is.null,expires_at.gt.now()'),
  ]);

  return {
    householdId,
    members: (membersResult.data ?? []) as unknown as HouseholdMember[],
    invites: (invitesResult.data ?? []) as unknown as HouseholdInvite[],
  };
}

// -----------------------------------------------------------------------------
// Send invite (via edge function for email notification)
// -----------------------------------------------------------------------------

export async function sendHouseholdInvite(
  householdId: string,
  email: string,
  name: string,
): Promise<InviteResult> {
  const { data, error } = await supabase.functions.invoke('send-household-invite', {
    body: {
      household_id: householdId,
      email: email.trim().toLowerCase(),
      name: name.trim(),
    },
  });

  if (error) {
    // Edge function invocation error
    return { success: false, error: error.message };
  }

  if (!data?.success) {
    return {
      success: false,
      error: data?.error || 'Failed to send invite',
      isDuplicate: data?.isDuplicate ?? false,
    };
  }

  return {
    success: true,
    emailSent: data.emailSent ?? false,
  };
}

// -----------------------------------------------------------------------------
// Cancel invite
// -----------------------------------------------------------------------------

export async function cancelHouseholdInvite(inviteId: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('household_invites')
    .delete()
    .eq('id', inviteId);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}
